import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { LiveAssistSession, LiveCapability } from "@backstory/protocol";
import type { Player } from "@backstory/player";
import { Dialog } from "../../components/ui";
import { AiBlockedTooltip, AiOutput, useAiCapability } from "../ai";
import { liveCaptureStatus, LEVEL_LABEL, type LiveCaptureHealth } from "../../lib/capture";
import { formatBytes, formatDuration } from "../../lib/format";
import { CAPABILITIES, CAPABILITY_LABEL, CONTROL_ERROR_TEXT, REQUESTABLE, type LiveState, type RungState } from "./model";
import type { VoiceMicrophone } from "./voice";

// ---------------------------------------------------------------------------
// Consent ladder
// ---------------------------------------------------------------------------

const RUNG_TEXT: Record<RungState, string> = {
  idle: "Not requested",
  requested: "Waiting for the user…",
  granted: "Granted",
  declined: "Declined",
  revoked: "Revoked",
  expired: "Expired",
};

/** Icons for the side rail: one glyph per capability, panel and action. */
export function RailIcon({ name }: { name: "support" | "cobrowse" | "voice" | "chat" | "copilot" | "send" | "mute" | "unmute" | "hangup" | "stop" | "capture" }) {
  const p = { width: 16, height: 16, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
  switch (name) {
    case "support":
      return <svg {...p}><path d="M4 14v-2a8 8 0 0 1 16 0v2" /><rect x="2.5" y="13" width="4" height="6" rx="1.6" /><rect x="17.5" y="13" width="4" height="6" rx="1.6" /><path d="M20 19v.5a2.5 2.5 0 0 1-2.5 2.5H14" /></svg>;
    case "cobrowse":
      return <svg {...p}><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8" /><path d="m10 8 5 5-2.2.6L14 16l-1.6.7-1-2.4L9.6 16Z" /></svg>;
    case "voice":
      return <svg {...p}><path d="M6.5 3.5h3l1.5 4-2 1.5a12 12 0 0 0 6 6l1.5-2 4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.5 5.7 2 2 0 0 1 6.5 3.5Z" /></svg>;
    case "chat":
      return <svg {...p}><path d="M4 5h16v11H9l-5 4V5Z" /></svg>;
    case "copilot":
      return <svg {...p}><path d="m12 3 1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9Z" /><path d="M18 16.5 18.8 18.6 21 19.5l-2.2.9-.8 2.1-.8-2.1-2.2-.9 2.2-.9Z" /></svg>;
    case "send":
      return <svg {...p}><path d="M4 12 20 4l-8 16-2-6Z" /></svg>;
    case "mute":
      return <svg {...p}><path d="M9 5a3 3 0 0 1 6 0v6a3 3 0 0 1-6 0Z" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg>;
    case "unmute":
      return <svg {...p}><path d="M9 5a3 3 0 0 1 6 0v5" /><path d="M5 11a7 7 0 0 0 10.5 6M12 18v3" /><path d="m4 3 16 18" /></svg>;
    case "hangup":
      return <svg {...p}><path d="M2.5 14.5a13 13 0 0 1 19 0l-2.5 2.5-3.5-1.5v-2a11 11 0 0 0-7 0v2L5 17Z" /><path d="m3 3 18 18" /></svg>;
    case "stop":
      return <svg {...p}><path d="M6 6l12 12M18 6 6 18" /></svg>;
    case "capture":
      return <svg {...p}><path d="M3 15l4-6 4 4 3-5 4 7" /><rect x="2.5" y="4.5" width="19" height="15" rx="2" /></svg>;
  }
}

const CAPABILITY_ICON: Record<string, "cobrowse" | "voice"> = { cobrowse: "cobrowse", voice: "voice" };

/** Shorter than RUNG_TEXT: the rail row has room for one word. */
const RUNG_CHIP: Record<RungState, string> = { ...RUNG_TEXT, requested: "Waiting…" };

export function AssistPanel({
  state,
  disabled,
  onRequest,
  onRevoke,
  microphones = [],
  microphoneId = "default",
  onMicrophone,
  unavailable = [],
}: {
  state: LiveState;
  disabled?: boolean;
  onRequest: (cap: LiveCapability) => void;
  onRevoke: (cap: LiveCapability) => void;
  microphones?: VoiceMicrophone[];
  microphoneId?: string;
  onMicrophone?: (deviceId: string) => void;
  /** Capabilities this deployment cannot offer, such as voice without LiveKit. */
  unavailable?: LiveCapability[];
}) {
  return (
    <section className="live-card" aria-label="Capability ladder">
      <div className="live-card-head">
        <RailIcon name="support" />
        <h3>Support</h3>
      </div>
      <ul className="live-caps">
        {REQUESTABLE.map((cap) => {
          const rung = state.rungs[cap];
          const off = unavailable.includes(cap);
          return (
            <Fragment key={cap}>
            <li className={`live-cap rung-${rung}`} data-testid={`rung-${cap}`}>
              <span className="live-cap-icon" aria-hidden="true">
                <RailIcon name={CAPABILITY_ICON[cap] ?? "cobrowse"} />
              </span>
              <span className="live-cap-name">{CAPABILITY_LABEL[cap]}</span>
              {rung !== "idle" && (
                <span className={`live-cap-state ${rung}`} data-testid={`rung-state-${cap}`} title={RUNG_TEXT[rung]}>
                  {RUNG_CHIP[rung]}
                </span>
              )}
              {rung === "granted" ? (
                <button className="btn ghost small icon-only" onClick={() => onRevoke(cap)} disabled={disabled} aria-label={`Stop ${CAPABILITY_LABEL[cap].toLowerCase()}`} title="Stop">
                  <RailIcon name="stop" />
                </button>
              ) : off ? (
                <span className="muted small" data-testid={`unavailable-${cap}`}>Not set up</span>
              ) : (
                <button className="btn small" onClick={() => onRequest(cap)} disabled={disabled || rung === "requested"} data-testid={`request-${cap}`}>
                  Request
                </button>
              )}
            </li>
            {cap === "voice" && !off && microphones.length > 1 && rung !== "requested" && (
              <li className="live-device-picker">
                <label htmlFor="live-support-microphone">Microphone</label>
                <select
                  id="live-support-microphone"
                  value={microphoneId}
                  onChange={(e) => onMicrophone?.(e.target.value)}
                  disabled={disabled}
                  data-testid="voice-microphone"
                  autoComplete="off"
                >
                  {microphones.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label}</option>)}
                </select>
              </li>
            )}
            </Fragment>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Annotation toolbar
// ---------------------------------------------------------------------------

export type AnnotationTool = "none" | "pen" | "arrow" | "rect" | "highlight" | "laser" | "text";

const TOOLS: Array<{ id: Exclude<AnnotationTool, "none">; label: string; key: string }> = [
  { id: "pen", label: "Pen", key: "P" },
  { id: "arrow", label: "Arrow", key: "A" },
  { id: "rect", label: "Rectangle", key: "R" },
  { id: "highlight", label: "Highlight element", key: "H" },
  { id: "laser", label: "Laser pointer", key: "L" },
  { id: "text", label: "Text", key: "T" },
];

function ToolIcon({ tool }: { tool: Exclude<AnnotationTool, "none"> | "clear" | "control" }) {
  const common = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
  switch (tool) {
    case "pen":
      return <svg {...common}><path d="m4 20 4.2-1 10.9-10.9a2.1 2.1 0 0 0-3-3L5.2 16 4 20Z" /><path d="m14.8 6.4 3 3" /></svg>;
    case "arrow":
      return <svg {...common}><path d="M5 19 19 5" /><path d="M10 5h9v9" /></svg>;
    case "rect":
      return <svg {...common}><rect x="4" y="5" width="16" height="14" rx="1.5" /></svg>;
    case "highlight":
      return <svg {...common}><path d="m7 15 8-8 3 3-8 8H7v-3Z" /><path d="M5 20h14" /></svg>;
    case "laser":
      return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" /></svg>;
    case "text":
      return <svg {...common}><path d="M5 5h14M12 5v14M8 19h8" /></svg>;
    case "clear":
      return <svg {...common}><path d="m4 15 8-10 7 6-7 9H8l-4-3v-2Z" /><path d="m9 16 4 4M14 9l4 4" /></svg>;
    case "control":
      return <svg {...common}><path d="m5 3 14 8.4-6.2 1.4L15 19l-2.6 1-2.4-6-4 3.3Z" /></svg>;
  }
}

/** Everything the agent does on the stage: drawing tools and taking control. */
export function StageToolbar({
  tool,
  onTool,
  onClear,
  disabled,
  controlGranted,
  controlOn,
  onControl,
}: {
  tool: AnnotationTool;
  onTool: (t: AnnotationTool) => void;
  onClear: () => void;
  disabled?: boolean;
  controlGranted: boolean;
  controlOn: boolean;
  onControl: (v: boolean) => void;
}) {
  return (
    <div className="live-toolbar" role="toolbar" aria-label="Session tools" data-disabled={disabled || undefined}>
      {TOOLS.map((t) => (
        <button
          key={t.id}
          type="button"
          className={`live-tool-button ${tool === t.id ? "active" : ""}`}
          aria-pressed={tool === t.id}
          aria-label={t.label}
          disabled={disabled}
          onClick={() => onTool(tool === t.id ? "none" : t.id)}
          title={`${t.label} (${t.key})`}
          data-testid={`tool-${t.id}`}
        >
          <ToolIcon tool={t.id} />
        </button>
      ))}
      <span className="live-tool-divider" aria-hidden="true" />
      <button type="button" className="live-tool-button" onClick={onClear} disabled={disabled} aria-label="Clear annotations" title="Clear annotations" data-testid="tool-clear">
        <ToolIcon tool="clear" />
      </button>
      <span className="live-tool-divider" aria-hidden="true" />
      <button
        type="button"
        className={`live-tool-button control ${controlOn ? "active" : ""}`}
        aria-pressed={controlOn}
        aria-label={controlOn ? "Stop controlling" : "Control the page"}
        disabled={!controlGranted}
        onClick={() => onControl(!controlOn)}
        title={controlGranted ? (controlOn ? "Stop controlling" : "Control the page") : "Support access is needed to control the page"}
        data-testid="control-toggle"
      >
        <ToolIcon tool="control" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capture health
// ---------------------------------------------------------------------------

/**
 * Always-visible verdict beside the LIVE badge. It sits next to the control
 * that says whether the agent is watching the present, because "am I seeing
 * everything?" is the same question as "am I seeing now?".
 */
export function CaptureChip({ health, now, offline }: { health: LiveCaptureHealth | null; now: number; offline?: boolean }) {
  const status = liveCaptureStatus(health, now, offline);
  return (
    <span className={`badge ${status.tone}`} title={status.detail || undefined} data-testid="live-capture-chip">
      {status.label}
    </span>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="live-capture-stat">
      <span>{label}</span>
      <strong>{children}</strong>
    </div>
  );
}

export function CaptureHealthCard({ health, now, offline }: { health: LiveCaptureHealth | null; now: number; offline?: boolean }) {
  const status = liveCaptureStatus(health, now, offline);
  const sample = health?.sample;
  const pct = (v: number) => `${(v * 100).toFixed(0)}%`;
  return (
    <section className="live-card live-capture" aria-label="Capture health" data-testid="live-capture">
      <div className="live-card-head">
        <RailIcon name="capture" />
        <h3>Capture</h3>
        <span className={`badge ${status.tone}`}>{status.label}</span>
      </div>
      {status.detail ? <p className="live-capture-detail">{status.detail}</p> : null}
      {health && (
        <div className="live-capture-stats">
          {health.worst !== health.level && <Stat label="Worst so far">{LEVEL_LABEL[health.worst]}</Stat>}
          {!status.stale && sample && (
            <>
              <Stat label="Upload backlog">{sample.drainSec < 0.1 ? "none" : `${sample.drainSec.toFixed(1)} s`}</Stat>
              <Stat label="Recorder memory">{pct(sample.memPct)}</Stat>
              <Stat label="Upload rate">{sample.online ? `${formatBytes(sample.goodputBps)}/s` : "offline"}</Stat>
              <Stat label="Page cost">{pct(sample.selfJank)}</Stat>
            </>
          )}
          {!status.stale && !sample && <p className="live-capture-note">Waiting for data…</p>}
          {health.droppedBytes > 0 && <Stat label="Dropped">{formatBytes(health.droppedBytes)}</Stat>}
          {health.gaps > 0 && <Stat label="Gaps">{health.gaps}</Stat>}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Control results
// ---------------------------------------------------------------------------

export function ControlResults({ state, onGuide }: { state: LiveState; onGuide?: (nodeId: number) => void }) {
  // Successful actions already appear in the Events tab. Keep only failures
  // here because they need an explanation or a recovery action.
  const last = state.controlResults.filter((r) => !r.ok).slice(-3).reverse();
  if (!last.length) return null;
  return (
    <ul className="live-results" aria-label="Control results">
      {last.map((r) => {
        const nodeId = r.command && "nodeId" in r.command ? r.command.nodeId : undefined;
        return (
          <li key={r.commandId} className={r.ok ? "ok" : "err"} data-testid="control-result">
            <span>{r.command ? describeCommand(r.command) : r.commandId}</span>
            <span className="live-guide">
              <span className="badge warn">{r.error ?? "failed"}</span> {r.error ? CONTROL_ERROR_TEXT[r.error] : ""}
              {r.error === "not_synthesizable" && nodeId != null && (
                <button className="btn ghost small" onClick={() => onGuide?.(nodeId)} data-testid="guide-user">
                  Ask the user to click here
                </button>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function describeCommand(c: NonNullable<LiveState["controlResults"][number]["command"]>): string {
  switch (c.kind) {
    case "click":
      return `click node #${c.nodeId}`;
    case "input":
      return `type into node #${c.nodeId}`;
    case "key":
      return `press ${c.key}`;
    case "scroll":
      return `scroll node #${c.nodeId}`;
    case "navigate":
      return `navigate to ${c.url}`;
    case "focus":
      return `focus node #${c.nodeId}`;
  }
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

export function ChatBox({ state, onSend, disabled }: { state: LiveState; onSend: (text: string) => void; disabled?: boolean }) {
  const [text, setText] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (typeof el.scrollTo === "function") el.scrollTo({ top: el.scrollHeight });
    else el.scrollTop = el.scrollHeight;
  }, [state.chat.length]);
  return (
    <section className="live-card live-chat" aria-label="Chat">
      <div className="live-card-head">
        <RailIcon name="chat" />
        <h3>Chat</h3>
      </div>
      <div className="live-chat-log" ref={listRef} data-testid="chat-log">
        {state.chat.length === 0 && <p className="muted small live-chat-empty">{disabled ? "Chat opens once the user grants support access." : "Say hello — the user sees this in their session."}</p>}
        {state.chat.map((l, i) => (
          <div key={i} className={`live-chat-line ${l.from}`}>
            <span className="who">{l.from === "agent" ? "You" : "User"}</span>
            <span>{l.text}</span>
          </div>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!text.trim()) return;
          onSend(text.trim());
          setText("");
        }}
        autoComplete="off"
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={disabled ? "Needs support access" : "Message…"}
          aria-label="Chat message"
          disabled={disabled}
          autoComplete="off"
        />
        <button className="btn small icon-only" type="submit" disabled={disabled || !text.trim()} aria-label="Send" title="Send">
          <RailIcon name="send" />
        </button>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Copilot (AI suggestions during the call)
// ---------------------------------------------------------------------------

export function Copilot({ suggestion, streaming, onSuggest, error }: { suggestion?: string; streaming: boolean; onSuggest: () => void; error?: string }) {
  const ai = useAiCapability();
  return (
    <section className="live-card live-copilot" aria-label="Copilot">
      <div className="live-card-head">
        <RailIcon name="copilot" />
        <h3>Copilot</h3>
        <AiBlockedTooltip ai={ai}>
          <button className="btn small" onClick={onSuggest} disabled={streaming || ai.blocked} title={ai.titleFor()} data-testid="copilot-suggest">
            {streaming ? "Thinking…" : "Suggest"}
          </button>
        </AiBlockedTooltip>
      </div>
      {ai.blocked && <p className="muted small">{ai.hint}.</p>}
      {error && <p className="error small">{error}</p>}
      {suggestion && (
        <AiOutput compact>
          <p className="live-copilot-text" data-testid="copilot-text">{suggestion}</p>
        </AiOutput>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Live events panel (feeds the DevTools dock's "Events" tab)
// ---------------------------------------------------------------------------

export function LiveEventsPanel({ player, version }: { player: Player | null; version: number }) {
  const rows = useMemo(() => {
    if (!player) return [];
    const events = player.timeline.events;
    const start = player.startTs;
    return events
      .filter((e) => ["pointer", "navigation", "error", "console", "network", "input", "control"].includes(e.event.type))
      .slice(-60)
      .reverse()
      .map((e) => ({ key: `${e.seq}:${e.idx}`, t: e.t - start, label: labelFor(e.event as unknown as Record<string, unknown>) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, version]);
  if (!player) return <p className="muted small">Waiting for the first chunk…</p>;
  return (
    <ul className="live-events" data-testid="live-events">
      {rows.map((r) => (
        <li key={r.key}>
          <span className="mono muted">{formatDuration(r.t)}</span>
          <span className="live-event-detail" title={r.label}>{r.label}</span>
        </li>
      ))}
    </ul>
  );
}

function labelFor(e: Record<string, unknown>): string {
  switch (e.type) {
    case "pointer":
      return e.kind === "click" ? `Click on node #${e.id ?? "?"}` : `Pointer ${e.kind}`;
    case "navigation":
      return `Navigate → ${e.url}`;
    case "error":
      return `Error: ${e.message}`;
    case "console": {
      const text = (e.args as string[] | undefined)?.[0] ?? "";
      if (text.startsWith("Live Assist ended")) return text;
      return `console.${e.level}: ${text}`;
    }
    case "network":
      return `${e.method ?? "GET"} ${e.url}${e.status ? ` → ${e.status}` : ""}`;
    case "input":
      return `Input on node #${e.id}`;
    case "control":
      return `Control: ${e.kind}`;
    default:
      return String(e.type);
  }
}

// ---------------------------------------------------------------------------
// Post-session summary
// ---------------------------------------------------------------------------

export function SummaryDialog({ open, onClose, assist, state, startedAt, sessionId }: { open: boolean; onClose: () => void; assist?: LiveAssistSession; state: LiveState; startedAt: number; sessionId: string }) {
  const duration = (state.ended?.at ?? Date.now()) - startedAt;
  const history = assist?.consentHistory ?? [];
  return (
    <Dialog open={open} onClose={onClose} title="Assist session ended" width={560}>
      <dl className="live-summary" data-testid="assist-summary">
        <dt>Duration</dt>
        <dd>{formatDuration(Math.max(0, duration))}</dd>
        <dt>Remote actions</dt>
        <dd>{assist?.controlActions ?? state.controlActions}</dd>
        <dt>Recorded</dt>
        <dd>{assist?.recorded ? "Yes (consent given)" : "No"}</dd>
        <dt>Ended by</dt>
        <dd>{state.ended?.reason ?? "agent"}</dd>
      </dl>
      <h4>Consent history</h4>
      <ConsentHistory history={history} fallback={state} />
      <p className="small">
        <a href={`/sessions/${encodeURIComponent(sessionId)}`}>Open the recorded session</a> to review the assist events in the Player.
      </p>
    </Dialog>
  );
}

export function ConsentHistory({ history, fallback }: { history: LiveAssistSession["consentHistory"]; fallback?: LiveState }) {
  if (!history.length && fallback) {
    return (
      <ul className="live-consent-history">
        {CAPABILITIES.filter((c) => fallback.rungs[c] !== "idle").map((c) => (
          <li key={c}>
            <strong>{CAPABILITY_LABEL[c]}</strong> — {RUNG_TEXT[fallback.rungs[c]]}
          </li>
        ))}
      </ul>
    );
  }
  if (!history.length) return <p className="muted small">No consent events.</p>;
  return (
    <ul className="live-consent-history">
      {history.map((h, i) => (
        <li key={i}>
          <span className="mono muted">{new Date(h.at).toLocaleTimeString()}</span> <strong>{CAPABILITY_LABEL[h.capability]}</strong>{" "}
          <span className={`badge ${h.granted ? "ok" : "warn"}`}>{h.granted ? "granted" : "declined / revoked"}</span> <span className="muted small">by {h.by}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A live session exists only while the SDK is streaming, so an agent who opens
 * a link a minute late is the normal case rather than an error. The console
 * chrome is replaced entirely: a header, an empty stage and a red alert box
 * would suggest something is still loading.
 */
export function LiveSessionUnavailable({ sessionId, forbidden, onRetry }: { sessionId: string; forbidden?: boolean; onRetry: () => void }) {
  return (
    <div className="live-gone" data-testid="live-unavailable">
      <div className="live-gone-card">
        <span className="live-gone-icon" aria-hidden="true">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            {forbidden ? (
              <>
                <rect x="4" y="10.5" width="16" height="10" rx="2" />
                <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
              </>
            ) : (
              <>
                <rect x="2.5" y="4" width="19" height="13" rx="2" />
                <path d="M8 21h8" />
                <path d="m3 3 18 18" />
              </>
            )}
          </svg>
        </span>
        <h2>{forbidden ? "You do not have access to this session" : "This session is not live"}</h2>
        <p>
          {forbidden
            ? "Ask an owner or admin of this project for access."
            : "It ended, the tab closed, or an agent closed it from the live list."}
        </p>
        <span className="live-gone-id mono" title={sessionId}>
          {sessionId}
        </span>
        <div className="live-gone-actions">
          <Link to="/live" className="btn primary">
            Live sessions
          </Link>
          {!forbidden && (
            <>
              <Link to="/sessions/$id" params={{ id: sessionId }} className="btn">
                Open the recording
              </Link>
              <button className="btn ghost" onClick={onRetry} data-testid="live-unavailable-retry">
                Try again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The recorder has gone quiet on a console that is still connected.
 *
 * Not a dead end: a hidden, backgrounded, or offline page resumes the same
 * session and this clears itself on the next chunk. Only a page that never
 * comes back makes the stored recording the whole story, so that link is
 * offered rather than followed.
 */
export function NotLiveNotice({ sessionId, ended, reason }: { sessionId: string; ended?: boolean; reason?: string }) {
  const detail = !ended
    ? "The page may be closed, backgrounded, or offline. Live resumes here if it comes back."
    : reason === "minutes"
      ? "The included minutes ran out, so this session ended. Anything still connected, including a voice call, has stopped."
      : reason === "unavailable"
        ? "Live Assist is no longer included for this account, so this session ended. Anything still connected, including a voice call, has stopped."
        : "Nothing more will arrive here.";
  return (
    <div className={`banner ${ended ? "info" : "warn"} live-notlive`} role="status" data-testid="live-notlive">
      <strong>{ended ? "This session has ended." : "No longer live."}</strong>
      <span>{detail}</span>
      <Link to="/sessions/$id" params={{ id: sessionId }} className="btn small">
        Open the recording
      </Link>
    </div>
  );
}

export function Panel({ title, children, actions }: { title: ReactNode; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="card live-panel">
      <div className="live-panel-head">
        <h3>{title}</h3>
        {actions}
      </div>
      {children}
    </section>
  );
}
